/**
 * MembershipProver — Phase 11: anonymous neighborhood-membership proofs.
 *
 * Two implementations behind one interface:
 *
 *   - PlaintextMembershipProver — deterministic, non-cryptographic. Used in
 *     tests and dev. A "proof" is just a signed assertion that a commitment
 *     is in the local group set; verification is set-membership.
 *
 *   - SemaphoreMembershipProver — production. Real Semaphore identity +
 *     Merkle group + zk-SNARK proof. Dynamic-imported so the Plaintext path
 *     doesn't pay the snarkjs cost.
 *
 * Both impls produce a `MembershipProof` blob with these fields:
 *   - nullifier:    deterministic hash(identity, externalNullifier), opaque
 *   - commitment:   the prover's commitment value
 *   - root:         Merkle root that the proof was generated against
 *   - merkleProof:  impl-specific bytes; verification material
 *   - impl:         which impl produced this (so the verifier picks the right code path)
 */

import { blake3 } from '@noble/hashes/blake3';

import type { SignerIdentity } from '../identity';

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC TYPES
// ─────────────────────────────────────────────────────────────────────────────

export type ProverImpl = 'plaintext' | 'semaphore';

/** A prover identity — opaque to callers; the impl knows its shape. */
export interface MembershipIdentity {
  impl: ProverImpl;
  /** Hex-encoded commitment. Stable across sessions when the secret is stable. */
  commitment: string;
  /** Impl-specific secret material — NEVER serialize or persist plaintext. */
  secret: Uint8Array;
}

export interface GenerateProofOptions {
  identity: MembershipIdentity;
  /** All commitments currently in the group. Order matters; same on prover + verifier. */
  group: string[];
  /** External nullifier (e.g., incidentId). Same caller proving for the same
   *  external nullifier gets the same nullifier output, enabling dedup. */
  externalNullifier: string;
  /** Application message bound to the proof (e.g., incident category + severity hash). */
  message: string;
}

export interface MembershipProof {
  impl: ProverImpl;
  /** Hex. */
  commitment: string;
  /** Hex. Merkle root the proof was made against. */
  root: string;
  /** Hex; deterministic from (identity, externalNullifier). */
  nullifier: string;
  /** Hex; binds the proof to a specific message. */
  message: string;
  /** Impl-specific proof bytes, base64-encoded. */
  proofData: string;
}

export interface VerifyProofOptions {
  proof: MembershipProof;
  /** Expected Merkle root (the verifier's current view of the group). Must match proof.root. */
  expectedRoot: string;
  /** Expected external nullifier (the verifier's externally-derived value). */
  externalNullifier: string;
  /** Expected message. */
  message: string;
}

export interface VerifyProofResult {
  ok: boolean;
  reason?: string;
}

export interface MembershipProver {
  readonly impl: ProverImpl;
  /** Create a stable identity from a secret seed (e.g., HKDF of the device key). */
  createIdentity(secret: Uint8Array): Promise<MembershipIdentity>;
  /** Compute the Merkle root for a given ordered list of commitments. */
  computeRoot(commitments: string[]): Promise<string>;
  generateProof(opts: GenerateProofOptions): Promise<MembershipProof>;
  verifyProof(opts: VerifyProofOptions): Promise<VerifyProofResult>;
}

// ─────────────────────────────────────────────────────────────────────────────
// PLAINTEXT IMPL — deterministic, non-cryptographic. For tests + dev.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Plaintext "proof": signature over (commitment || externalNullifier || message)
 * by the application SignerIdentity. The group root is just BLAKE3 of the
 * ordered list of commitments. Verification:
 *   1. proof.commitment is in the group (so root matches)
 *   2. proof.root == BLAKE3(group)
 *   3. proof.nullifier == BLAKE3(identity.secret || externalNullifier)
 *   4. message binding matches
 *
 * Cryptographically this is NOT anonymous — the verifier sees which commitment.
 * It's a wiring placeholder so the rest of the ALDS plumbing can be tested
 * without paying the Semaphore boot cost.
 */
export class PlaintextMembershipProver implements MembershipProver {
  readonly impl = 'plaintext' as const;
  readonly #signer: SignerIdentity | null;

  constructor(signer: SignerIdentity | null = null) {
    this.#signer = signer;
  }

  async createIdentity(secret: Uint8Array): Promise<MembershipIdentity> {
    const commitment = bytesToHex(blake3(secret));
    return { impl: 'plaintext', commitment, secret: new Uint8Array(secret) };
  }

  async computeRoot(commitments: string[]): Promise<string> {
    if (commitments.length === 0) return ZERO_ROOT;
    const sorted = [...commitments].sort();
    const concat = new TextEncoder().encode(sorted.join('|'));
    return bytesToHex(blake3(concat));
  }

  async generateProof(opts: GenerateProofOptions): Promise<MembershipProof> {
    if (opts.identity.impl !== 'plaintext') {
      throw new Error(`PlaintextMembershipProver: identity impl mismatch (${opts.identity.impl})`);
    }
    const root = await this.computeRoot(opts.group);
    const nullifier = bytesToHex(blake3(concatBytes(opts.identity.secret, new TextEncoder().encode(opts.externalNullifier))));
    const messageHash = bytesToHex(blake3(new TextEncoder().encode(opts.message)));
    const proofBytes = new TextEncoder().encode(JSON.stringify({
      commitment: opts.identity.commitment,
      root,
      nullifier,
      messageHash,
    }));
    let sig = '';
    if (this.#signer !== null) {
      const sigBytes = await this.#signer.sign(proofBytes, { environment: 'hub', purpose: 'membership' });
      sig = bytesToHex(sigBytes);
    }
    return {
      impl: 'plaintext',
      commitment: opts.identity.commitment,
      root,
      nullifier,
      message: messageHash,
      proofData: bytesToBase64(concatBytes(proofBytes, new TextEncoder().encode(`|sig:${sig}`))),
    };
  }

  async verifyProof(opts: VerifyProofOptions): Promise<VerifyProofResult> {
    if (opts.proof.impl !== 'plaintext') {
      return { ok: false, reason: `impl mismatch (${opts.proof.impl}, expected plaintext)` };
    }
    if (opts.proof.root !== opts.expectedRoot) {
      return { ok: false, reason: `root mismatch` };
    }
    const expectedMessageHash = bytesToHex(blake3(new TextEncoder().encode(opts.message)));
    if (opts.proof.message !== expectedMessageHash) {
      return { ok: false, reason: `message hash mismatch` };
    }
    // Nullifier check is done at the verifier's side by comparing against
    // the externalNullifier they expected. Plaintext can't recompute the
    // nullifier without the prover's secret, but it CAN check the format.
    if (!/^[0-9a-f]{64}$/.test(opts.proof.nullifier)) {
      return { ok: false, reason: `nullifier malformed` };
    }
    // The externalNullifier is bound via the nullifier derivation in the
    // prover. We can't recompute without the secret; trust the prover here.
    // SemaphoreMembershipProver does proper zk verification.
    void opts.externalNullifier;
    return { ok: true };
  }
}

const ZERO_ROOT = '0'.repeat(64);

// ─────────────────────────────────────────────────────────────────────────────
// SEMAPHORE IMPL — production. Dynamically imports @semaphore-protocol/*.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SemaphoreMembershipProver wires the canonical Semaphore v4 stack:
 *   - Identity         : @semaphore-protocol/identity
 *   - Group            : @semaphore-protocol/group (LeanIMT)
 *   - generateProof    : @semaphore-protocol/proof
 *   - verifyProof      : @semaphore-protocol/proof
 *
 * Imports are dynamic so Plaintext-only builds (tests, demos) don't pay the
 * snarkjs bundle cost. Proof generation in the browser takes ~1-3s on a
 * 2024 phone; in node tests it's similar.
 */
export class SemaphoreMembershipProver implements MembershipProver {
  readonly impl = 'semaphore' as const;
  #cachedIdentity: { secret: string; identity: unknown } | null = null;

  async createIdentity(secret: Uint8Array): Promise<MembershipIdentity> {
    const secretHex = bytesToHex(secret);
    const { Identity } = await import('@semaphore-protocol/identity');
    // Semaphore v4 Identity takes a string seed; we pass our secret hex.
    const id = new Identity(secretHex);
    this.#cachedIdentity = { secret: secretHex, identity: id };
    const commitment = (id as { commitment: bigint }).commitment.toString(16);
    return { impl: 'semaphore', commitment, secret: new Uint8Array(secret) };
  }

  async computeRoot(commitments: string[]): Promise<string> {
    const { Group } = await import('@semaphore-protocol/group');
    const g = new Group();
    for (const c of commitments) g.addMember(BigInt('0x' + c));
    return g.root.toString(16);
  }

  async generateProof(opts: GenerateProofOptions): Promise<MembershipProof> {
    if (opts.identity.impl !== 'semaphore') {
      throw new Error(`SemaphoreMembershipProver: identity impl mismatch (${opts.identity.impl})`);
    }
    const { Identity } = await import('@semaphore-protocol/identity');
    const { Group } = await import('@semaphore-protocol/group');
    const { generateProof } = await import('@semaphore-protocol/proof');

    const idSecret = bytesToHex(opts.identity.secret);
    const identity = this.#cachedIdentity?.secret === idSecret
      ? this.#cachedIdentity.identity as InstanceType<typeof Identity>
      : new Identity(idSecret);
    this.#cachedIdentity = { secret: idSecret, identity };

    const group = new Group();
    for (const c of opts.group) group.addMember(BigInt('0x' + c));

    // External nullifier and message both have to be bigints in Semaphore v4.
    const externalNullifierBigInt = bytesToBigInt(blake3(new TextEncoder().encode(opts.externalNullifier)));
    const messageBigInt = bytesToBigInt(blake3(new TextEncoder().encode(opts.message)));

    // generateProof signature: (identity, groupOrMerkleProof, message, scope, treeDepth?)
    // Semaphore v4 inferred scope/treeDepth from group; signature is
    //   generateProof(identity, groupOrMerkleProof, message, scope, snarkArtifacts?)
    const proof = await generateProof(
      identity,
      group,
      messageBigInt,
      externalNullifierBigInt,
    );

    const proofNullifier = (proof as unknown as { nullifier: bigint | string }).nullifier;
    return {
      impl: 'semaphore',
      commitment: opts.identity.commitment,
      root: group.root.toString(16),
      nullifier: typeof proofNullifier === 'bigint' ? proofNullifier.toString(16) : String(proofNullifier),
      message: messageBigInt.toString(16),
      proofData: bytesToBase64(new TextEncoder().encode(JSON.stringify(proof, bigintReplacer))),
    };
  }

  async verifyProof(opts: VerifyProofOptions): Promise<VerifyProofResult> {
    if (opts.proof.impl !== 'semaphore') {
      return { ok: false, reason: `impl mismatch (${opts.proof.impl}, expected semaphore)` };
    }
    if (opts.proof.root !== opts.expectedRoot) {
      return { ok: false, reason: `root mismatch` };
    }
    try {
      const { verifyProof } = await import('@semaphore-protocol/proof');
      const proof = JSON.parse(new TextDecoder().decode(base64ToBytes(opts.proof.proofData))) as Parameters<typeof verifyProof>[0];
      const ok = await verifyProof(proof);
      return ok ? { ok: true } : { ok: false, reason: 'zk-SNARK verification failed' };
    } catch (e) {
      return { ok: false, reason: `verify threw: ${e instanceof Error ? e.message : String(e)}` };
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function bytesToHex(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i++) s += b[i]!.toString(16).padStart(2, '0');
  return s;
}

function concatBytes(...arrs: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const a of arrs) total += a.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrs) { out.set(a, off); off += a.length; }
  return out;
}

function bytesToBase64(b: Uint8Array): string {
  let s = '';
  const chunk = 0x8000;
  for (let i = 0; i < b.length; i += chunk) {
    s += String.fromCharCode(...b.subarray(i, Math.min(i + chunk, b.length)));
  }
  return btoa(s);
}

function base64ToBytes(s: string): Uint8Array {
  const raw = atob(s);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function bytesToBigInt(b: Uint8Array): bigint {
  let n = 0n;
  for (let i = 0; i < b.length; i++) n = (n << 8n) | BigInt(b[i]!);
  return n;
}

/** JSON.stringify replacer that turns bigints into strings (Semaphore proofs hold them). */
function bigintReplacer(_k: string, v: unknown): unknown {
  return typeof v === 'bigint' ? v.toString() : v;
}
